import { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  Filter,
  UserCog,
  UserX,
  Trash2,
  Shield,
  Building2,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  Edit
} from 'lucide-react';
import Card from '../../components/common/Card';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import api from '../../services/api';

interface PlatformUser {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  primary_organization_id: string;
  primary_organization_name: string;
  memberships: UserMembership[];
}

interface UserMembership {
  id: string;
  organization_id: string;
  organization_name: string;
  role_id: string;
  role_name: string;
  role_key: string;
  status: string;
  joined_at: string;
  last_accessed_at?: string;
}

interface Role {
  id: string;
  role_name: string;
  role_key: string;
  description?: string;
}

interface OrgOption {
  id: string;
  name: string;
}

interface Statistics {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  totalMemberships: number;
  usersWithMultipleOrgs: number;
}

export default function PlatformUserManagementPage() {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [organizations, setOrganizations] = useState<OrgOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageSize] = useState(20);
  
  // Modal state
  const [selectedUser, setSelectedUser] = useState<PlatformUser | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showRoleChangeModal, setShowRoleChangeModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState<UserMembership | null>(null);
  const [newRoleId, setNewRoleId] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchStatistics();
    fetchRoles();
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, filterActive, selectedOrg]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      };
      
      if (searchTerm) params.search = searchTerm;
      if (filterActive !== undefined) params.isActive = filterActive;
      if (selectedOrg) params.organizationId = selectedOrg;

      const response = await api.get('/platform/users', { params });
      setUsers(response.data.data || []);
      setTotalUsers(response.data.pagination?.total || 0);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/platform/users/statistics');
      setStatistics(response.data.data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data.data || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations/public/list');
      setOrganizations(response.data.data || []);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleChangeRole = async () => {
    if (!selectedMembership || !newRoleId) return;

    try {
      setProcessingAction(true);
      await api.put(`/platform/users/memberships/${selectedMembership.id}/role`, {
        roleId: newRoleId,
        reason: actionReason
      });
      
      setSuccess('User role changed successfully');
      setShowRoleChangeModal(false);
      setNewRoleId('');
      setActionReason('');
      fetchUsers();
      
      if (selectedUser) {
        const response = await api.get(`/platform/users/${selectedUser.id}`);
        setSelectedUser(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to change user role');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRemoveFromOrg = async (membership: UserMembership) => {
    if (!confirm(`Remove ${selectedUser?.full_name} from ${membership.organization_name}?`)) {
      return;
    }

    const reason = prompt('Reason for removal (optional):');

    try {
      setProcessingAction(true);
      const response = await api.delete(`/platform/users/memberships/${membership.id}`, {
        data: { reason }
      });
      
      if (response.data.data?.userDeleted) {
        setSuccess('User removed from organization and deleted from platform (no remaining memberships)');
        setShowUserDetails(false);
        setSelectedUser(null);
      } else {
        setSuccess('User removed from organization successfully');
      }
      
      fetchUsers();
      fetchStatistics();
      
      if (selectedUser && !response.data.data?.userDeleted) {
        const userResponse = await api.get(`/platform/users/${selectedUser.id}`);
        setSelectedUser(userResponse.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to remove user from organization');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDeleteUser = async (user: PlatformUser) => {
    if (!confirm(`Delete ${user.full_name} (${user.email}) from the entire platform? This action cannot be undone.`)) {
      return;
    }

    const reason = prompt('Reason for deletion (required):');
    if (!reason) {
      setError('Reason is required for user deletion');
      return;
    }

    try {
      setProcessingAction(true);
      await api.delete(`/platform/users/${user.id}`, {
        data: { reason }
      });
      
      setSuccess('User deleted from platform successfully');
      setShowUserDetails(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStatistics();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete user');
    } finally {
      setProcessingAction(false);
    }
  };

  const totalPages = Math.ceil(totalUsers / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Platform User Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage users across all organizations
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert
          type="error"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          type="success"
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {statistics.totalUsers}
                </p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Users</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {statistics.activeUsers}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Inactive Users</p>
                <p className="text-2xl font-bold text-gray-600 dark:text-gray-400 mt-1">
                  {statistics.inactiveUsers}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-gray-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Memberships</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {statistics.totalMemberships}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-purple-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Multi-Org Users</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {statistics.usersWithMultipleOrgs}
                </p>
              </div>
              <Shield className="w-8 h-8 text-orange-500" />
            </div>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by email or name..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <select
                value={filterActive === undefined ? '' : filterActive.toString()}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilterActive(value === '' ? undefined : value === 'true');
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="">All Status</option>
                <option value="true">Active Only</option>
                <option value="false">Inactive Only</option>
              </select>

              <select
                value={selectedOrg}
                onChange={(e) => {
                  setSelectedOrg(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="">All Organizations</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>

              <Button
                variant="secondary"
                onClick={() => {
                  setSearchTerm('');
                  setFilterActive(undefined);
                  setSelectedOrg('');
                  setCurrentPage(1);
                }}
              >
                <Filter className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Users List */}
      <Card>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No users found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Primary Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Memberships
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                            <span className="text-primary-600 dark:text-primary-400 font-medium">
                              {user.full_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.full_name || 'No name'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {user.primary_organization_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {user.memberships.length} organization{user.memberships.length !== 1 ? 's' : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserDetails(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteUser(user)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <Modal onClose={() => { setShowUserDetails(false); setSelectedUser(null); }} panelClassName="max-w-4xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  User Details
                </h2>
                <button
                  onClick={() => {
                    setShowUserDetails(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  User Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {selectedUser.full_name || 'No name'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {selectedUser.email}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedUser.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}>
                      {selectedUser.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Last Login</p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Created</p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {new Date(selectedUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Primary Organization</p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {selectedUser.primary_organization_name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Memberships */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Organization Memberships ({selectedUser.memberships.length})
                </h3>
                <div className="space-y-3">
                  {selectedUser.memberships.map((membership) => (
                    <div
                      key={membership.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="w-5 h-5 text-gray-400" />
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {membership.organization_name}
                            </h4>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Role: </span>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {membership.role_name}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Status: </span>
                              <span className={`font-medium ${
                                membership.status === 'active'
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-gray-600 dark:text-gray-400'
                              }`}>
                                {membership.status}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Joined: </span>
                              <span className="text-gray-900 dark:text-white">
                                {new Date(membership.joined_at).toLocaleDateString()}
                              </span>
                            </div>
                            {membership.last_accessed_at && (
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Last Access: </span>
                                <span className="text-gray-900 dark:text-white">
                                  {new Date(membership.last_accessed_at).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedMembership(membership);
                              setNewRoleId(membership.role_id);
                              setShowRoleChangeModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Change Role
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRemoveFromOrg(membership)}
                            disabled={processingAction}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Danger Zone */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
                  Danger Zone
                </h3>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-red-900 dark:text-red-200 mb-1">
                        Delete User from Platform
                      </h4>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Permanently delete this user and all their data. This action cannot be undone.
                      </p>
                    </div>
                    <Button
                      variant="danger"
                      onClick={() => handleDeleteUser(selectedUser)}
                      disabled={processingAction}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete User
                    </Button>
                  </div>
                </div>
              </div>
            </div>
        </Modal>
      )}

      {/* Role Change Modal */}
      {showRoleChangeModal && selectedMembership && (
        <Modal onClose={() => setShowRoleChangeModal(false)} panelClassName="max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Change User Role
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Organization
                </label>
                <p className="text-gray-900 dark:text-white">
                  {selectedMembership.organization_name}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Role
                </label>
                <p className="text-gray-900 dark:text-white">
                  {selectedMembership.role_name}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Role *
                </label>
                <select
                  value={newRoleId}
                  onChange={(e) => setNewRoleId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason (optional)
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Why are you changing this user's role?"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRoleChangeModal(false);
                  setSelectedMembership(null);
                  setNewRoleId('');
                  setActionReason('');
                }}
                disabled={processingAction}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleChangeRole}
                disabled={!newRoleId || processingAction}
              >
                {processingAction ? <Spinner size="sm" /> : 'Change Role'}
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
}

// Made with Bob
